import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import { useSessionStorage } from "usehooks-ts";

import { removeSessionValue } from "@punto/frontend-shared/utils/session-storage";

/** Query param carrying the customer scope, e.g. `?customer=<customerId>`. */
export const CUSTOMER_SCOPE_PARAM = "customer";

/** Single session slot — deliberately not per screen and not per view. */
export const CUSTOMER_SCOPE_STORAGE_KEY = "customer-scope";

const EMPTY_SCOPE: string[] = [];

/** Drop the stored scope. Used when switching organization invalidates it. */
export const clearStoredCustomerScope = (): void =>
  removeSessionValue(CUSTOMER_SCOPE_STORAGE_KEY);

export interface CustomerScope {
  /** `[]` is every customer, `[id]` is one — the shape the filters and API speak. */
  customerIds: string[];
  setCustomerIds: (next: string[]) => void;
}

/**
 * The customer scope — the sidebar's "Empresa" selector — is **not a table
 * filter** (P-1585 feedback). It is a persistent scope, and that distinction is
 * what this hook exists to enforce:
 *
 * - it survives moving between views, because it is not part of the table's
 *   `?state=` blob — that blob is session-persisted *per view id*, so a customer
 *   living inside it would mean every view remembering its own, and switching
 *   views resurrecting a stale one (or silently dropping the current one);
 * - picking a customer therefore never marks a view "Modificado", since
 *   `useListViews` only ever compares table filters.
 *
 * It is still persisted in the URL, so a scoped list stays shareable and
 * reloadable — but as its own `?customer=` param, never inside `?state=`.
 *
 * The session slot is the source of truth; the param is read once, at mount, to
 * let a pasted link win, and projected back out from then on. Keeping the param
 * as a continuous *input* would make "todas las empresas" unselectable from a
 * scoped URL: navigations are applied in a transition, so there is a render where
 * the slot is already cleared but the param is not, and that render would read as
 * a fresh deep link and put the customer straight back.
 */
export const useCustomerScope = (): CustomerScope => {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  // Read raw: the seed below only acts on a truthy value, so a bare `?customer=`
  // is treated as unset rather than as a customer with an empty id.
  const urlCustomer = urlSearchParams.get(CUSTOMER_SCOPE_PARAM);

  const [customer, setCustomer] = useSessionStorage<string | null>(
    CUSTOMER_SCOPE_STORAGE_KEY,
    null,
  );

  const customerIds = useMemo(
    () => (customer ? [customer] : EMPTY_SCOPE),
    [customer],
  );

  const setCustomerIds = useCallback(
    (next: string[]) => {
      // The selector can only express one customer; the URL follows from the
      // projection effect below.
      setCustomer(next.length === 1 ? next[0] : null);
    },
    [setCustomer],
  );

  // ONE effect reconciles the session slot and the param in both directions —
  // it used to be two, a mount-only seed plus a projection, which also meant the
  // seed's `setCustomer` and the projection's param write raced within the same
  // commit: the projection saw the pre-seed value and briefly published the
  // stale scope before the adoption re-ran it.
  //
  // An effect is mandatory here, not incidental. The param is an external store
  // this hook does not own, and it has to be repaired after navigations the user
  // performed and this hook never saw — switching view, opening a row's drawer —
  // which drop the query string without the scope changing. So it cannot collapse
  // into `setCustomerIds`. Nor can either direction move into render: the
  // `useSessionStorage` setter is a `useEventCallback`, which throws outright when
  // called while rendering.
  //
  // `replace` throughout, and only when the param actually differs: a scope is a
  // lens on the list, not a place, and restoring a param the user never removed
  // must not leave a history entry for Back to land on.
  const hasSeeded = useRef(false);
  useEffect(() => {
    // A pasted/deep-linked param wins over whatever the session remembers —
    // once. `useSessionStorage` ignores its own initial value when a stored one
    // exists, so this cannot be expressed as that default. Returning early is
    // what stops the projection below from immediately overwriting the param
    // with the value being replaced; see the doc comment on why the param must
    // stop being an input after this.
    if (!hasSeeded.current) {
      hasSeeded.current = true;

      if (urlCustomer && urlCustomer !== customer) {
        setCustomer(urlCustomer);
        return;
      }
    }

    if (customer === urlCustomer) return;

    setUrlSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        if (customer) params.set(CUSTOMER_SCOPE_PARAM, customer);
        else params.delete(CUSTOMER_SCOPE_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [customer, urlCustomer, setCustomer, setUrlSearchParams]);

  return { customerIds, setCustomerIds };
};
